import React, { useState, useEffect, useRef } from 'react';
import {
  useCreateExpense,
  useUpdateExpense,
  useExpenseCategories,
  Expense,
} from '../hooks/useExpenses.ts';
import { X } from 'lucide-react';

type Receipt = {
  name: string;
}
interface ReceiptPreview {
  file: File | Receipt | null;
  preview: string;
  name: string;
  existing?: boolean;
};


const ExpenseForm = ({
  expense,
  onClose,
  onManageCategories,
}: {
  expense?: Expense;
  onClose: () => void;
  onManageCategories?: () => void;
}) => {
  const [form, setForm] = useState({
    date: '',
    category_id: '',
    description: '',
    amount: '',
    receipt: [] as File[],
  });
  const [previews, setPreviews] = useState<ReceiptPreview[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');

  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const { data: categories = [], isLoading: categoriesLoading } = useExpenseCategories();

  const orphanCategory =
    expense?.category_id != null &&
    expense.category_id > 0 &&
    !categories.some((c) => c.id === expense.category_id)
      ? { id: expense.category_id, label: expense.category || `Category #${expense.category_id}` }
      : null;

  useEffect(() => {
    if (expense) {
      const matchedId =
        expense.category_id != null && expense.category_id > 0
          ? String(expense.category_id)
          : categories.find((c) => c.name === expense.category)?.id != null
            ? String(categories.find((c) => c.name === expense.category)!.id)
            : '';

      setForm({
        date: expense.date || '',
        category_id: matchedId,
        description: expense.description || '',
        amount: expense.amount?.toString() || '',
        receipt: [],
      });

      const receiptPaths = Array.isArray(expense.receipt_path) ? expense.receipt_path : [];
      const existingReceipts: ReceiptPreview[] = receiptPaths.map((path: string) => ({
        file: null,
        preview: path.endsWith('.pdf') ? '' : `${(process.env.REACT_APP_API_BASE_URL).slice(0, -4)}/storage/${path.replace('public/', '')}`,
        name: path.split('/').pop() || 'file',
        existing: true,
      }));

      setPreviews(existingReceipts);
    } else {
      setForm({ date: '', category_id: '', description: '', amount: '', receipt: [] });
      setPreviews([]);
    }
  }, [expense, categories]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setForm((prev) => ({
        ...prev,
        receipt: [...prev.receipt, ...newFiles],
      }));
      const newPreviews = newFiles.map((file) => ({
        file,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : "",
        name: file.name,
      }));
      setPreviews((prev) => [...prev, ...newPreviews]);
      if(fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const removeFile = (index: number) => {
    setPreviews(prev => prev.filter((_, i) => i !== index));

    setForm(prev => ({
      ...prev,
      receipt: prev.receipt.filter((_, i) => !previews[i] || !previews[i].existing || i !== index),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.date || !form.category_id || !form.amount) {
      setError('Date, category, and amount are required.');
      return;
    }
    if (expense) {
      const newFiles = form.receipt.filter(item => item instanceof File);

      const retainedReceipts = previews
        .filter(p => p.existing && !form.receipt.some(f => f.name === p.name))
        .map(p => p.name);
      const payload = new FormData();
      payload.append('date', form.date);
      payload.append('category_id', form.category_id);
      payload.append('description', form.description);
      payload.append('amount', form.amount);
      if (retainedReceipts.length > 0) {
        payload.append('retained_receipts', JSON.stringify(retainedReceipts));
      }
      for (const file of newFiles) {
        payload.append('receipt[]', file);
      }
      updateExpense.mutate({ 
        id: expense.id,
        formData: payload
      }, { 
        onSuccess: onClose 
      });
    } else {
      const payload = new FormData();
      payload.append('date', form.date);
      payload.append('category_id', form.category_id);
      payload.append('description', form.description);
      payload.append('amount', form.amount);
      if (form.receipt.length > 0) {
        for (const file of form.receipt) {
          if (file instanceof File) {
            payload.append('receipt[]', file);
          }
        }
      }
      createExpense.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-xl font-bold mb-2">{expense ? 'Edit Expense' : 'Add Expense'}</h2>
      {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <select
            name="category_id"
            value={form.category_id}
            onChange={handleChange}
            required
            disabled={categoriesLoading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">{categoriesLoading ? 'Loading…' : 'Select category'}</option>
            {orphanCategory ? (
              <option value={String(orphanCategory.id)}>{orphanCategory.label} (unlisted)</option>
            ) : null}
            {categories.map((cat) => (
              <option key={cat.id} value={String(cat.id)}>
                {cat.name}
              </option>
            ))}
          </select>
          {!categoriesLoading && categories.length === 0 && (
            <p className="mt-1 text-xs text-amber-700">
              No categories defined for this group.
              {onManageCategories ? (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={onManageCategories}
                    className="text-blue-600 underline font-medium"
                  >
                    Manage categories
                  </button>
                </>
              ) : null}
            </p>
          )}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
          <input
            type="number"
            name="amount"
            value={form.amount}
            onChange={handleChange}
            required
            min="0"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Receipt (optional)</label>
          <input
            type="file"
            name="receipt"
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            ref={fileInputRef} 
            multiple
          />
        </div>
      </div>
      {/* files preview */}
      {previews.length > 0 && (
        <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-3">Uploaded Files</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {previews.map((item, index) => (
              <div key={index} className="relative group">
                {item.preview ? (
                  <img
                    src={item.preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-24 object-cover rounded-md border border-gray-300"
                  />
                ) : (
                  <div className="w-full h-24 bg-white border border-gray-300 rounded-md flex items-center justify-center">
                    <span className="text-xs text-gray-600 text-center px-2">📄 { item.name ? item.name.split('.').pop()?.toUpperCase() : item.file.name.split('.').pop()?.toUpperCase()}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  aria-label="Remove file"
                >
                  <X size={16} />
                </button>
                <p className="text-xs text-gray-600 mt-1 truncate">{item.name || item.file.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={createExpense.isPending || updateExpense.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {expense ? 'Save Changes' : 'Add Expense'}
        </button>
      </div>
    </form>
  );
};

export default ExpenseForm; 
